/**
 * Modal d'historique de modifications pour UNE ligne (= 1 étudiant + 1 matière + 1 semestre).
 *
 * Combine l'historique :
 *   - de la NoteDevoir (note1, note2, absent1, absent2)
 *   - de la NoteExamen (note_examen, absent) — session ordinaire ET rattrapage
 *
 * Affiche pour chaque entrée : qui a modifié, quand, champ, ancien → nouveau,
 * motif et commentaire éventuels.
 */
import React, { useMemo } from 'react';
import { X, Clock, User, ArrowRight, FileText, Loader, BookOpen, Award } from 'lucide-react';
import { useGetHistoriqueQuery as useGetHistoriqueDevoirsQuery } from '../../services/notesDevoirsApi';
import { useGetHistoriqueQuery as useGetHistoriqueExamensQuery } from '../../services/notesExamensApi';

interface Props {
  open: boolean;
  onClose: () => void;
  noteDevoirId: number | null;
  noteExamenId: number | null;
  etudiantNom?: string;
  etudiantMatricule?: string;
  matiereCode?: string;
  matiereNom?: string;
}

interface UnifiedEntry {
  id: number;
  source: 'devoir' | 'examen';
  champ_modifie: string;
  ancienne_valeur: string | null;
  nouvelle_valeur: string | null;
  motif: string;
  motif_display: string;
  commentaire: string;
  modifie_par_nom: string | null;
  date_modification: string;
}

const CHAMP_LABEL: Record<string, string> = {
  note1: 'Devoir 1',
  note2: 'Devoir 2',
  absent1: 'Absence Devoir 1',
  absent2: 'Absence Devoir 2',
  note_examen: 'Note examen',
  absent: 'Absence examen',
};

const HistoriqueNoteModal: React.FC<Props> = ({
  open, onClose, noteDevoirId, noteExamenId,
  etudiantNom, etudiantMatricule, matiereCode, matiereNom,
}) => {
  const { data: histDevoirs = [], isFetching: loadingD } = useGetHistoriqueDevoirsQuery(
    noteDevoirId ? { note_devoir_id: noteDevoirId } : ({} as any),
    { skip: !open || !noteDevoirId },
  );
  // L'API examens accepte note_examen_id (côté backend, le filter use note_examen_id)
  const { data: histExamens = [], isFetching: loadingE } = useGetHistoriqueExamensQuery(
    noteExamenId ? { note_examen_id: noteExamenId } as any : ({} as any),
    { skip: !open || !noteExamenId },
  );

  const entries: UnifiedEntry[] = useMemo(() => {
    const out: UnifiedEntry[] = [];
    histDevoirs.forEach(h => out.push({
      id: h.id, source: 'devoir',
      champ_modifie: h.champ_modifie,
      ancienne_valeur: h.ancienne_valeur,
      nouvelle_valeur: h.nouvelle_valeur,
      motif: h.motif, motif_display: h.motif_display,
      commentaire: h.commentaire,
      modifie_par_nom: h.modifie_par_nom,
      date_modification: h.date_modification,
    }));
    histExamens.forEach(h => out.push({
      id: 1_000_000 + h.id,  // évite collision id
      source: 'examen',
      champ_modifie: h.champ_modifie,
      ancienne_valeur: h.ancienne_valeur,
      nouvelle_valeur: h.nouvelle_valeur,
      motif: h.motif, motif_display: h.motif_display,
      commentaire: h.commentaire,
      modifie_par_nom: h.modifie_par_nom,
      date_modification: h.date_modification,
    }));
    out.sort((a, b) => b.date_modification.localeCompare(a.date_modification));
    return out;
  }, [histDevoirs, histExamens]);

  const loading = loadingD || loadingE;

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col">
        <div className="px-5 py-3 border-b dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Clock size={18} className="text-indigo-600" />
              Historique des modifications
            </h3>
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
              {etudiantMatricule && <span className="font-mono">{etudiantMatricule}</span>}
              {etudiantNom && <span> · {etudiantNom}</span>}
              {matiereCode && (
                <span> · <span className="font-semibold text-blue-700 dark:text-blue-300">{matiereCode}</span> {matiereNom && `— ${matiereNom}`}</span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-8">
              <Loader className="animate-spin mx-auto text-indigo-600" size={28} />
              <p className="mt-2 text-sm text-gray-500">Chargement de l'historique…</p>
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-500">
              {noteDevoirId || noteExamenId ? (
                <>Aucune modification enregistrée pour cette note.</>
              ) : (
                <>Cette note n'a pas encore été créée — aucun historique.</>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {entries.map(e => {
                const isAbsent = e.champ_modifie.startsWith('absent');
                return (
                  <div
                    key={`${e.source}-${e.id}`}
                    className={`border rounded p-2.5 text-xs ${
                      e.source === 'devoir'
                        ? 'border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10'
                        : 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10'
                    }`}
                  >
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded flex items-center gap-1 ${
                        e.source === 'devoir'
                          ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                          : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
                      }`}>
                        {e.source === 'devoir' ? <BookOpen size={10} /> : <Award size={10} />}
                        {e.source === 'devoir' ? 'Devoir' : 'Examen'}
                      </span>
                      <span className="font-semibold text-gray-800 dark:text-gray-200">
                        {CHAMP_LABEL[e.champ_modifie] || e.champ_modifie}
                      </span>
                      <span className="ml-auto text-[10px] text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <Clock size={10} />
                        {new Date(e.date_modification).toLocaleString('fr-FR', {
                          dateStyle: 'short', timeStyle: 'short',
                        })}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                      <span className={`px-2 py-0.5 rounded font-mono ${
                        isAbsent
                          ? (e.ancienne_valeur === 'True' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300')
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}>
                        {e.ancienne_valeur === '' || e.ancienne_valeur === null
                          ? <em className="text-gray-400">vide</em>
                          : (isAbsent ? (e.ancienne_valeur === 'True' ? 'AB' : '—') : e.ancienne_valeur)}
                      </span>
                      <ArrowRight size={12} className="text-gray-400" />
                      <span className={`px-2 py-0.5 rounded font-mono font-semibold ${
                        isAbsent
                          ? (e.nouvelle_valeur === 'True' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200')
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
                      }`}>
                        {e.nouvelle_valeur === '' || e.nouvelle_valeur === null
                          ? <em className="text-gray-400">vide</em>
                          : (isAbsent ? (e.nouvelle_valeur === 'True' ? 'AB' : '—') : e.nouvelle_valeur)}
                      </span>
                    </div>

                    <div className="mt-1.5 flex items-center gap-3 text-[11px] text-gray-600 dark:text-gray-400 flex-wrap">
                      <span className="flex items-center gap-1">
                        <User size={10} />
                        {e.modifie_par_nom || '— inconnu —'}
                      </span>
                      {e.motif && (
                        <span className="px-1.5 py-0.5 text-[10px] bg-gray-100 dark:bg-gray-700 rounded">
                          {e.motif_display || e.motif}
                        </span>
                      )}
                    </div>

                    {e.commentaire && (
                      <div className="mt-1.5 px-2 py-1 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded text-[11px] text-gray-700 dark:text-gray-300 italic flex items-start gap-1.5">
                        <FileText size={11} className="text-gray-400 mt-0.5 shrink-0" />
                        <span>{e.commentaire}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-5 py-2.5 border-t dark:border-gray-700 flex justify-between items-center text-xs">
          <span className="text-gray-500 dark:text-gray-400">
            {entries.length > 0 && `${entries.length} modification${entries.length > 1 ? 's' : ''}`}
          </span>
          <button
            onClick={onClose}
            className="px-3 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default HistoriqueNoteModal;
